import { useCallback, useEffect, useState } from "react";

/**
 * A per-browser preference: React state mirrored into localStorage under
 * `key`, JSON-encoded. Never room state — these are viewing and listening
 * choices (time format, lane height, follow, the record offset …) that
 * belong to the person at this keyboard, not to the Set.
 *
 * `accept` is the type guard for what's stored: anything else (a missing
 * key, a private window, a value from an older build) yields `fallback`.
 * The key may change (a per-jam pref as the route moves to another jam);
 * the value is re-read when it does.
 */
export function usePref<T>(key: string, fallback: T, accept: (v: unknown) => v is T): [T, (next: T) => void] {
  const read = useCallback((): T => {
    try {
      return decodePref(localStorage.getItem(key), fallback, accept);
    } catch {
      return fallback;
    }
  }, [key, fallback, accept]);
  const [value, setValue] = useState<T>(read);
  useEffect(() => setValue(read()), [read]);
  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // storage unavailable — the value still holds for this page
      }
    },
    [key],
  );
  return [value, set];
}

/**
 * What a stored value means. JSON as this hook writes it; else the
 * encodings earlier builds used — a bare string ("bars", "compact") or a
 * boolean as "1"/"0" — so an upgrade never resets a choice; else the
 * fallback.
 */
export function decodePref<T>(raw: string | null, fallback: T, accept: (v: unknown) => v is T): T {
  if (raw === null) return fallback;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return accept(raw) ? raw : fallback;
  }
  if (accept(parsed)) return parsed;
  if (typeof fallback === "boolean" && (parsed === 1 || parsed === 0)) return (parsed === 1) as T;
  return fallback;
}

/** Guards for the common shapes. */
export const isBoolean = (v: unknown): v is boolean => typeof v === "boolean";
export const isFiniteNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
export function oneOf<const T extends readonly string[]>(choices: T): (v: unknown) => v is T[number] {
  return (v): v is T[number] => typeof v === "string" && (choices as readonly string[]).includes(v);
}
